// Base libs
import { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { I18nComponent } from "react/utils/I18nComponent";
import {
    GuxButton,
    GuxDropdown,
    GuxDropdownMulti,
    GuxIcon,
    GuxListbox,
    GuxListboxMulti,
    GuxOption,
    GuxOptionMulti,
} from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";
import {
    AssistanceConditionOperatorEnum,
    EvaluationQuestionTypeEnum,
} from "@genesys/cloud-frontend/models";

// General Common
import { useAppDispatch, useAppSelector } from "app/hooks";

import FormType from "enums/FormType";
import AutomatedScoringType from "enums/AutomatedScoringType";
import { DefaultLimits } from "constants/DefaultLimits";

// Feature common

import { selectAllPublishedTopics } from "features/topics/topicsSlice";
import { DeleteButton } from "features/common/ActionIcons";
import { useStrings } from "features/i18n/useStrings";

// Feature slices
import {
    selectAssistanceConditions,
    selectFormType,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    qualityFormActions,
    selectTopicFilteringFactorFromAssistanceConditions,
    updateAssistanceConditionsWithValidTopics,
} from "features/qualityForm/qualityFormSlice";
import { TopicDetail } from "features/scorable/scorableSlice";
import { selectCanUserViewSTATopics } from "features/auth/currentUserSlice";

// Quality form common
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";
import {
    selectIsAssistanceConditionsHavingInsufficientTopics,
    selectIsAssistanceConditionsHavingTooManyTopics,
    selectInvalidTopicsInAssistanceCondition,
    selectIsAnyTopicInAssistanceConditionInvalid,
} from "features/qualityForm/qualityFormValidations";

// Locales
import strings from "./EvaluationAssistance.strings";

// Style
import "./EvaluationAssistance.less";

function AssistanceConditionOperator({
    index,
    answerId,
    isMultipleSelect = false,
}: {
    index: number;
    answerId: string;
    isMultipleSelect: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const assistanceConditionOperator = useAppSelector((state) => {
        return selectAssistanceConditions(state, answerId, isMultipleSelect)?.[
            index
        ].operator;
    });
    return (
        <div className="AssistanceConditionOperator">
            <GuxDropdown
                value={assistanceConditionOperator}
                onInput={(e) => {
                    const conditionalOperator = (e.target as HTMLInputElement)
                        .value as AssistanceConditionOperatorEnum;
                    dispatch(
                        qualityFormActions.updateAssistanceConditionOperator({
                            answerId,
                            isMultipleSelect,
                            index,
                            operator: conditionalOperator,
                        })
                    );
                }}
                data-testid="assistance-condition-operator"
            >
                <GuxListbox aria-label={t.labels.dropDownOptions}>
                    {Object.values(AssistanceConditionOperatorEnum).map(
                        (operator) => (
                            <GuxOption key={operator} value={operator}>
                                {t.operator[operator]}
                            </GuxOption>
                        )
                    )}
                </GuxListbox>
            </GuxDropdown>
        </div>
    );
}

function TopicSelector({
    answerId,
    isMultipleSelect,
    index,
    topicIdIdxMap,
    hasSameTopicInDifferentConditions,
}: {
    answerId: string;
    isMultipleSelect: boolean;
    index: number;
    topicIdIdxMap: { [key: string]: number };
    hasSameTopicInDifferentConditions: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const selectedTopicIds = useAppSelector(
        (state) =>
            selectAssistanceConditions(state, answerId, isMultipleSelect)?.[
                index
            ].topicIds
    );

    const topicDetails = useAppSelector(
        (state) => selectAllPublishedTopics(state),
        shallowEqual
    );

    const [sortedTopics, setSortedTopics] = useState<
        TopicDetail[] | undefined
    >();
    const getReorderedTopics = () => {
        const selectedIdSet = new Set(selectedTopicIds);
        const unselectedTopicDetails: TopicDetail[] = [];
        const selectedTopicDetails: TopicDetail[] = [];
        topicDetails.forEach((topicDetail) => {
            selectedIdSet.has(topicDetail?.id ?? "")
                ? selectedTopicDetails.push(topicDetail)
                : unselectedTopicDetails.push(topicDetail);
        });
        return (selectedTopicDetails ?? []).concat(
            unselectedTopicDetails ?? []
        );
    };
    // reordering onMount only
    useEffect(() => {
        setSortedTopics(getReorderedTopics());
    }, []);

    return (
        <div className="TopicSelector">
            {sortedTopics && (
                <GuxDropdownMulti
                    filterType="starts-with"
                    placeholder={t.labels.selectTopics}
                    value={selectedTopicIds?.join(",") ?? ""}
                    onInput={(e) => {
                        const selectedValue = (e.target as HTMLInputElement)
                            .value;
                        const selectedTopics =
                            selectedValue === undefined
                                ? undefined
                                : selectedValue.split(",");
                        dispatch(
                            updateAssistanceConditionsWithValidTopics(
                                answerId,
                                isMultipleSelect,
                                index,
                                selectedTopics
                            )
                        );
                    }}
                    onGuxcollapsed={() => {
                        setSortedTopics(getReorderedTopics());
                    }}
                >
                    <GuxListboxMulti aria-label={t.labels.topics}>
                        {sortedTopics
                            .filter(
                                (topic) =>
                                    hasSameTopicInDifferentConditions ||
                                    (topic.id !== undefined &&
                                        (topicIdIdxMap[topic.id] ===
                                            undefined ||
                                            topicIdIdxMap[topic.id] === index))
                            )
                            .map((topic, index) => (
                                <GuxOptionMulti
                                    value={topic.id}
                                    key={topic.id ?? index}
                                >
                                    {topic.name}
                                </GuxOptionMulti>
                            ))}
                    </GuxListboxMulti>
                </GuxDropdownMulti>
            )}
        </div>
    );
}

export function AddConditionButton({
    answerId,
    isMultipleSelect,
    disabled = false,
}: {
    answerId: string;
    isMultipleSelect: boolean;
    disabled?: boolean;
}) {
    const t = useStrings(strings);

    const dispatch = useAppDispatch();
    const onAdd = () => {
        dispatch(
            qualityFormActions.addEvaluationAssistanceCondition({
                answerId,
                isMultipleSelect,
            })
        );
    };

    return (
        <div className="AddCondition">
            <GuxButton
                accent="ghost"
                disabled={toGuxDisabled(disabled)}
                aria-disabled={toGuxDisabled(disabled)}
                onClick={() => onAdd()}
                data-testid="add-condition-button"
            >
                <GuxIcon
                    icon-name={"fa/circle-plus-regular"}
                    decorative={true}
                />
                <span>{t.labels.addConditions}</span>
            </GuxButton>
        </div>
    );
}

function Condition({
    answerId,
    isMultipleSelect,
    index,
    topicIdIdxMap,
    hasSameTopicInDifferentConditions,
}: {
    answerId: string;
    isMultipleSelect: boolean;
    index: number;
    topicIdIdxMap: { [key: string]: number };
    hasSameTopicInDifferentConditions: boolean;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const hasDeletedTopics = useAppSelector((state) => {
        const deletedTopics = selectInvalidTopicsInAssistanceCondition(
            state,
            answerId,
            index,
            isMultipleSelect
        );
        return deletedTopics.length > 0;
    });

    return (
        <div
            className={`condition ${hasDeletedTopics ? "error" : ""}`}
            data-testid="condition"
        >
            <div className="condition-content">
                <I18nComponent
                    i18nKey="qualityFormEditor.evaluationAssistance.labels.condition"
                    substitutions={{
                        AssistanceConditionOperator: (
                            <AssistanceConditionOperator
                                index={index}
                                answerId={answerId}
                                isMultipleSelect={isMultipleSelect}
                            />
                        ),
                        TopicSelector: (
                            <TopicSelector
                                answerId={answerId}
                                isMultipleSelect={isMultipleSelect}
                                index={index}
                                topicIdIdxMap={topicIdIdxMap}
                                hasSameTopicInDifferentConditions={
                                    hasSameTopicInDifferentConditions
                                }
                            />
                        ),
                        Prefix: (
                            <span className="operation">
                                {index === 0 ? t.labels.select : t.labels.and}
                            </span>
                        ),
                    }}
                />
                <div className="delete">
                    <DeleteButton
                        onDelete={() => {
                            dispatch(
                                qualityFormActions.deleteEvaluationAssistanceCondition(
                                    { answerId, index, isMultipleSelect }
                                )
                            );
                        }}
                    />
                </div>
            </div>
            {hasDeletedTopics && (
                <div className="condition-error">
                    <ErrorStatement errorMessage={t.errors.topicDeleted} />
                </div>
            )}
        </div>
    );
}

function Conditions({
    questionId,
    answerId,
    isMultipleSelect,
}: {
    questionId: string;
    answerId: string;
    isMultipleSelect: boolean;
}) {
    // NOTE: No need to check whether STA topic view permission is here because
    // Conditions is private and only used by EvaluationAssistance which already
    // checked for the permission before calling Conditions.
    const t = useStrings(strings);

    const isEAModeSelected = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        return (
            question &&
            question.automatedScoringType ===
                AutomatedScoringType.EVALUATION_ASSISTANCE
        );
    });
    const canAddAssistanceConditions = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        if (
            question?.type === EvaluationQuestionTypeEnum.MultipleSelectQuestion
        ) {
            // Multiple select can always add EAs at each option.
            return true;
        } else {
            const answerOptions = question?.answerOptions;
            let answerOptionIdWithEA;
            const stateAnswerOptions = state.qualityForm.data.answerOptions;
            if (stateAnswerOptions) {
                answerOptions?.some((answerOptionId) => {
                    if (
                        stateAnswerOptions[answerOptionId]
                            .assistanceConditions !== undefined
                    ) {
                        answerOptionIdWithEA = answerOptionId;
                        return true;
                    }
                });
            }
            const canAdd =
                !answerOptionIdWithEA || answerOptionIdWithEA === answerId;
            return isEAModeSelected && canAdd;
        }
    });

    const conditionsSize =
        useAppSelector((state) => {
            return selectAssistanceConditions(state, answerId, isMultipleSelect)
                ?.length;
        }) ?? 0;

    const canAddMoreTopics = useAppSelector((state) => {
        return (
            (selectAssistanceConditions(
                state,
                answerId,
                isMultipleSelect
            )?.flatMap((condition) => condition.topicIds).length ?? 0) < 10
        );
    });
    const hasTooManyTopics = useAppSelector((state) => {
        return selectIsAssistanceConditionsHavingTooManyTopics(
            state,
            answerId,
            isMultipleSelect
        );
    });
    const hasInsufficientTopics = useAppSelector((state) =>
        selectIsAssistanceConditionsHavingInsufficientTopics(
            state,
            answerId,
            isMultipleSelect
        )
    );

    const { topicIdIdxMap, hasSameTopicInDifferentConditions } = useAppSelector(
        (state) =>
            selectTopicFilteringFactorFromAssistanceConditions(
                state,
                answerId,
                isMultipleSelect
            )
    );

    return (
        <div className="Conditions" data-testid="conditions">
            {[...Array(conditionsSize)].map((condition, index) => {
                return (
                    <Condition
                        answerId={answerId}
                        isMultipleSelect={isMultipleSelect}
                        index={index}
                        key={index}
                        topicIdIdxMap={topicIdIdxMap}
                        hasSameTopicInDifferentConditions={
                            hasSameTopicInDifferentConditions
                        }
                    />
                );
            })}
            {conditionsSize > 0 && (
                <>
                    {hasTooManyTopics && (
                        <ErrorStatement
                            errorMessage={t.errors.tooManyTopics({
                                maxAssistanceConditionTopics:
                                    DefaultLimits.maxAssistanceConditionTopics,
                            })}
                        />
                    )}
                    {hasInsufficientTopics && (
                        <ErrorStatement
                            errorMessage={t.errors.insufficientTopics}
                        />
                    )}
                    {hasSameTopicInDifferentConditions && (
                        <ErrorStatement
                            errorMessage={
                                t.errors.sameTopicInDifferentConditions
                            }
                        />
                    )}
                </>
            )}
            {canAddAssistanceConditions && (
                <AddConditionButton
                    answerId={answerId}
                    isMultipleSelect={isMultipleSelect}
                    disabled={!canAddMoreTopics || hasInsufficientTopics}
                />
            )}
        </div>
    );
}

export function EvaluationAssistance({
    questionId,
    answerId,
    isMultipleSelect,
}: {
    questionId: string;
    answerId: string;
    isMultipleSelect: boolean;
}) {
    const t = useStrings(strings);

    const canUserViewTopics: boolean = useAppSelector(
        selectCanUserViewSTATopics
    );
    const isEvaluationForm: boolean = useAppSelector(
        (state) => selectFormType(state) === FormType.EVALUATION
    );

    const showInvalidAssistedQMError = useAppSelector((state) => {
        return (
            !canUserViewTopics &&
            selectIsAnyTopicInAssistanceConditionInvalid(
                state,
                answerId,
                isMultipleSelect
            )
        );
    });
    return (
        <>
            {isEvaluationForm && (
                <div className="EvaluationAssistance">
                    {canUserViewTopics && (
                        <Conditions
                            questionId={questionId}
                            answerId={answerId}
                            isMultipleSelect={isMultipleSelect}
                        />
                    )}
                    {showInvalidAssistedQMError && (
                        <ErrorStatement
                            errorMessage={t.errors.assistedAnswerOptionInvalid}
                        />
                    )}
                </div>
            )}
        </>
    );
}
